import type {ParsedUrlQuery} from 'node:querystring';
import type {SearchOption} from './useTodosData';

/**
 * URLのentryManagementIdパラメータを更新するためのURL文字列を生成
 * @param currentUrl - 現在のURL
 * @param entryManagementId - 設定するエントリー管理ID（nullの場合は削除）
 * @returns 更新後のURL文字列
 */
export const buildUrlWithEntryManagementId = (
  currentUrl: string,
  entryManagementId: number | null,
): string => {
  const url = new URL(currentUrl);
  if (entryManagementId) {
    url.searchParams.set('entry_management_id', String(entryManagementId));
  } else {
    url.searchParams.delete('entry_management_id');
  }
  return url.toString();
};

/**
 * router.queryから指定したキーを除いたクエリ文字列を生成
 * @param query - router.queryのようなクエリパラメータ
 * @param excludeKey - 除外するキー
 * @returns クエリ文字列（該当パラメータがない場合は空文字）
 */
export const buildQueryStringWithout = (
  query: ParsedUrlQuery,
  excludeKey: string,
): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (key === excludeKey || value === undefined) continue;
    for (const item of Array.isArray(value) ? value : [value]) {
      params.append(key, item);
    }
  }
  return params.toString();
};

/**
 * searchOptionsから指定したentryManagementIdに一致するオプションを検索
 * @param searchOptions - 検索候補オプションのリスト
 * @param entryManagementId - 検索するエントリー管理ID
 * @returns 一致するオプション、見つからない場合はundefined
 */
export const findEntryManagementOption = (
  searchOptions: SearchOption[],
  entryManagementId: number,
): SearchOption | undefined => {
  return searchOptions.find(
    (opt) =>
      opt.type === 'company' && opt.userEntryManagementID === entryManagementId,
  );
};

/**
 * 初期化処理の結果を表す型
 */
export type InitializationResult =
  | {action: 'skip'; reason: 'already_initialized' | 'router_not_ready'}
  | {action: 'complete'; searchWord: string | null}
  | {action: 'wait'; reason: 'loading_options'};

/**
 * entryManagementIdパラメータからの初期化処理ロジック
 *
 * @param isInitialized - 初期化済みフラグ
 * @param isRouterReady - ルーター準備完了フラグ
 * @param entryManagementIdFromUrl - URLのentryManagementIdパラメータ
 * @param searchOptions - 検索候補オプションのリスト
 * @returns 初期化処理の結果
 */
export const determineInitializationAction = (
  isInitialized: boolean,
  isRouterReady: boolean,
  entryManagementIdFromUrl: string | string[] | undefined,
  searchOptions: SearchOption[],
): InitializationResult => {
  // 初期化済みの場合はスキップ
  if (isInitialized) {
    return {action: 'skip', reason: 'already_initialized'};
  }

  // ルーター未準備の場合はスキップ
  if (!isRouterReady) {
    return {action: 'skip', reason: 'router_not_ready'};
  }

  // URLにentryManagementIdがない場合は初期化完了（検索ワードなし）
  if (!entryManagementIdFromUrl) {
    return {action: 'complete', searchWord: null};
  }

  // APIデータ読み込み待ち
  if (searchOptions.length === 0) {
    return {action: 'wait', reason: 'loading_options'};
  }

  const entryManagementId = Number(entryManagementIdFromUrl);

  // 無効なentryManagementIdの場合は初期化完了（検索ワードなし）
  if (Number.isNaN(entryManagementId)) {
    return {action: 'complete', searchWord: null};
  }

  // マッチするエントリー管理を探す
  const option = findEntryManagementOption(searchOptions, entryManagementId);

  if (option) {
    // 該当ToDoあり - 検索ワードを設定
    return {action: 'complete', searchWord: option.value};
  }

  // 該当ToDoなし - 全てのToDoを表示
  return {action: 'complete', searchWord: null};
};

/**
 * searchWordの変化からURLクリアが必要かを判定
 * @param prevSearchWord - 前回の検索ワード
 * @param currentSearchWord - 現在の検索ワード
 * @returns URLからentryManagementIdを削除すべきかどうか
 */
export const shouldClearUrlEntryManagementId = (
  prevSearchWord: string,
  currentSearchWord: string,
): boolean => {
  const wasNotEmpty = prevSearchWord !== '';
  const isNowEmpty = currentSearchWord === '';
  return wasNotEmpty && isNowEmpty;
};
